import { useState } from 'react'
import { isAxiosError } from 'axios'
import { ArrowLeftIcon, PlusIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useCreateSistemaArchitectureNode,
  useCreateSistemaChannel,
  useCreateSistemaComponent,
  useDeleteSistemaArchitectureNode,
  useDeleteSistemaChannel,
  useDeleteSistemaComponent,
  useGetSafetyFunction,
  useUpdateSafetyFunction,
  useUpdateSistemaArchitectureNode,
  useUpdateSistemaChannel,
  useUpdateSistemaComponent,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { NotFoundView } from '@/features/errors/not-found-view'
import { ComplianceSubPageHeader } from '../../components/ComplianceSubPageHeader'
import { ComplianceSubPageTitle } from '../../components/ComplianceSubPageTitle'
import { DeleteArchitectureEntityDialog } from './DeleteArchitectureEntityDialog'
import { SafetyFunctionDetailInfoWidgets } from './SafetyFunctionDetailInfoWidgets'
import {
  SistemaBlockDiagram,
  type ArchitectureDialogState,
} from './SistemaBlockDiagram'
import { SistemaChannelSheet } from './SistemaChannelSheet'
import { SistemaComponentSheet } from './SistemaComponentSheet'
import { SistemaNodeSheet } from './SistemaNodeSheet'
import {
  toSafetyFunctionPayload,
  type SafetyFunctionFormData,
} from './safetyFunctionForm.schema'
import {
  toSistemaChannelPayload,
  toSistemaComponentPayload,
  toSistemaNodePayload,
} from './sistemaArchitectureForm.schema'

export function SafetyFunctionDetailPage() {
  const params = useParams<{
    machineryDirectiveComplianceId: string
    safetyFunctionId: string
  }>()
  const complianceId = params.machineryDirectiveComplianceId
  const safetyFunctionId = params.safetyFunctionId
  const navigate = useNavigate()
  const { t } = useTranslation('machineryDirectiveCompliance')

  const {
    data: safetyFunction,
    isLoading,
    error,
  } = useGetSafetyFunction(complianceId, safetyFunctionId)

  const [dialogState, setDialogState] = useState<ArchitectureDialogState>(null)
  const [activeComponentId, setActiveComponentId] = useState<string | null>(
    null
  )

  const updateSafetyFunction = useUpdateSafetyFunction(complianceId ?? '')
  const createComponent = useCreateSistemaComponent(
    complianceId ?? '',
    safetyFunctionId ?? ''
  )
  const updateComponent = useUpdateSistemaComponent(
    complianceId ?? '',
    safetyFunctionId ?? ''
  )
  const deleteComponent = useDeleteSistemaComponent(
    complianceId ?? '',
    safetyFunctionId ?? ''
  )
  const createChannel = useCreateSistemaChannel(
    complianceId ?? '',
    safetyFunctionId ?? ''
  )
  const updateChannel = useUpdateSistemaChannel(
    complianceId ?? '',
    safetyFunctionId ?? ''
  )
  const deleteChannel = useDeleteSistemaChannel(
    complianceId ?? '',
    safetyFunctionId ?? ''
  )
  const createNode = useCreateSistemaArchitectureNode(
    complianceId ?? '',
    safetyFunctionId ?? ''
  )
  const updateNode = useUpdateSistemaArchitectureNode(
    complianceId ?? '',
    safetyFunctionId ?? ''
  )
  const deleteNode = useDeleteSistemaArchitectureNode(
    complianceId ?? '',
    safetyFunctionId ?? ''
  )

  const isMutationPending =
    updateSafetyFunction.isPending ||
    createComponent.isPending ||
    updateComponent.isPending ||
    deleteComponent.isPending ||
    createChannel.isPending ||
    updateChannel.isPending ||
    deleteChannel.isPending ||
    createNode.isPending ||
    updateNode.isPending ||
    deleteNode.isPending

  const closeDialog = () => {
    setDialogState(null)
    setActiveComponentId(null)
  }

  const handleFieldUpdate = (
    data: SafetyFunctionFormData,
    onSuccess?: () => void
  ) => {
    if (!safetyFunctionId) {
      return
    }

    updateSafetyFunction.mutate(
      {
        safetyFunctionId,
        ...toSafetyFunctionPayload(data),
      },
      {
        onSuccess: () => {
          toast.success(t('actions.saved'))
          onSuccess?.()
        },
        onError: (err) => {
          toast.error(err.message ?? t('safetyFunctionsPage.updateFailure'))
        },
      }
    )
  }

  if (isLoading) {
    return (
      <div className='space-y-4'>
        <Skeleton className='h-8 w-64' />
        <Skeleton className='h-48 w-full' />
      </div>
    )
  }

  if (error != null || safetyFunction == null) {
    const isNotFound =
      error == null || (isAxiosError(error) && error.response?.status === 404)

    return (
      <div className='flex flex-1 flex-col items-center justify-center py-16'>
        <NotFoundView
          showCode={false}
          title={t('safetyFunctionsPage.notFound')}
          description={isNotFound ? undefined : error?.message}
          primaryAction={{
            label: t('safetyFunctionsPage.backToList'),
            onClick: () => navigate('..', { relative: 'path' }),
          }}
        />
      </div>
    )
  }

  const editingComponent =
    dialogState?.kind === 'component-edit' ? dialogState.component : null
  const editingChannel =
    dialogState?.kind === 'channel-edit' ? dialogState.channel : null
  const editingNode =
    dialogState?.kind === 'block-edit' || dialogState?.kind === 'element-edit'
      ? dialogState.node
      : null
  const editingNodeKind =
    dialogState?.kind === 'element-create' ||
    dialogState?.kind === 'element-edit'
      ? 'ELEMENT'
      : 'BLOCK'

  return (
    <div className='space-y-6'>
      <ComplianceSubPageHeader>
        <ComplianceSubPageTitle className='flex items-center gap-2 truncate'>
          <Link
            to='..'
            relative='path'
            className='text-sm text-muted-foreground hover:text-foreground'
          >
            <ArrowLeftIcon className='size-4' />
          </Link>
          {safetyFunction.name}
        </ComplianceSubPageTitle>
      </ComplianceSubPageHeader>

      <div className='space-y-4'>
        <SafetyFunctionDetailInfoWidgets
          safetyFunction={safetyFunction}
          isUpdating={updateSafetyFunction.isPending}
          onUpdate={handleFieldUpdate}
        />

        <section className='space-y-4'>
          <div className='flex items-start justify-between gap-4'>
            <div>
              <h3 className='text-base font-semibold'>
                {t('safetyFunctionsPage.architecture.title')}
              </h3>
              <p className='text-sm text-muted-foreground'>
                {t('safetyFunctionsPage.architecture.description')}
              </p>
            </div>
            <Button
              type='button'
              size='sm'
              variant='outline'
              className='shrink-0'
              disabled={isMutationPending}
              onClick={() => setDialogState({ kind: 'component-create' })}
            >
              <PlusIcon className='mr-2 size-4' />
              {t('safetyFunctionsPage.architecture.addComponent')}
            </Button>
          </div>
          <SistemaBlockDiagram
            safetyFunction={safetyFunction}
            isMutationPending={isMutationPending}
            onOpenDialog={(state) => {
              if (state.kind === 'channel-create') {
                setActiveComponentId(state.componentId)
              }
              setDialogState(state)
            }}
          />
        </section>
      </div>

      <SistemaComponentSheet
        key={editingComponent?.id ?? 'component-create'}
        isOpen={
          dialogState?.kind === 'component-create' ||
          dialogState?.kind === 'component-edit'
        }
        onClose={closeDialog}
        component={editingComponent}
        isSaving={createComponent.isPending || updateComponent.isPending}
        onSave={async (payload) => {
          try {
            if (dialogState?.kind === 'component-edit') {
              await updateComponent.mutateAsync({
                componentId: dialogState.component.id,
                ...toSistemaComponentPayload(payload),
              })
            } else {
              await createComponent.mutateAsync(
                toSistemaComponentPayload(payload)
              )
            }
            toast.success(t('actions.saved'))
            closeDialog()
          } catch (err: unknown) {
            const message =
              err instanceof Error
                ? err.message
                : t('safetyFunctionsPage.updateFailure')
            toast.error(message)
          }
        }}
      />

      <SistemaChannelSheet
        key={editingChannel?.id ?? 'channel-create'}
        isOpen={
          dialogState?.kind === 'channel-create' ||
          dialogState?.kind === 'channel-edit'
        }
        onClose={closeDialog}
        channel={editingChannel}
        isSaving={createChannel.isPending || updateChannel.isPending}
        onSave={async (payload) => {
          try {
            if (dialogState?.kind === 'channel-edit') {
              await updateChannel.mutateAsync({
                channelId: dialogState.channel.id,
                ...toSistemaChannelPayload(payload),
              })
            } else if (
              dialogState?.kind === 'channel-create' &&
              activeComponentId
            ) {
              await createChannel.mutateAsync({
                componentId: activeComponentId,
                ...toSistemaChannelPayload(payload),
              })
            }
            toast.success(t('actions.saved'))
            closeDialog()
          } catch (err: unknown) {
            const message =
              err instanceof Error
                ? err.message
                : t('safetyFunctionsPage.updateFailure')
            toast.error(message)
          }
        }}
      />

      <SistemaNodeSheet
        key={editingNode?.id ?? `node-create-${editingNodeKind}`}
        isOpen={
          dialogState?.kind === 'block-create' ||
          dialogState?.kind === 'block-edit' ||
          dialogState?.kind === 'element-create' ||
          dialogState?.kind === 'element-edit'
        }
        onClose={closeDialog}
        node={editingNode}
        kind={editingNodeKind}
        isSaving={createNode.isPending || updateNode.isPending}
        onSave={async (payload) => {
          try {
            if (
              dialogState?.kind === 'block-edit' ||
              dialogState?.kind === 'element-edit'
            ) {
              await updateNode.mutateAsync({
                nodeId: dialogState.node.id,
                ...toSistemaNodePayload(payload),
              })
            } else if (dialogState?.kind === 'block-create') {
              await createNode.mutateAsync({
                channelId: dialogState.channelId,
                kind: 'BLOCK',
                ...toSistemaNodePayload(payload),
              })
            } else if (dialogState?.kind === 'element-create') {
              await createNode.mutateAsync({
                channelId: dialogState.channelId,
                kind: 'ELEMENT',
                parentNodeId: dialogState.parentNodeId,
                ...toSistemaNodePayload(payload),
              })
            }
            toast.success(t('actions.saved'))
            closeDialog()
          } catch (err: unknown) {
            const message =
              err instanceof Error
                ? err.message
                : t('safetyFunctionsPage.updateFailure')
            toast.error(message)
          }
        }}
      />

      <DeleteArchitectureEntityDialog
        isOpen={dialogState?.kind === 'component-delete'}
        onClose={closeDialog}
        isDeleting={deleteComponent.isPending}
        titleKey='safetyFunctionsPage.architecture.deleteComponentTitle'
        descriptionKey='safetyFunctionsPage.architecture.deleteComponentDescription'
        onConfirm={async () => {
          if (dialogState?.kind !== 'component-delete') {
            return
          }
          try {
            await deleteComponent.mutateAsync(dialogState.componentId)
            toast.success(t('safetyFunctionsPage.deleteSuccess'))
            closeDialog()
          } catch (err: unknown) {
            const message =
              err instanceof Error
                ? err.message
                : t('safetyFunctionsPage.deleteFailure')
            toast.error(message)
          }
        }}
      />

      <DeleteArchitectureEntityDialog
        isOpen={dialogState?.kind === 'channel-delete'}
        onClose={closeDialog}
        isDeleting={deleteChannel.isPending}
        titleKey='safetyFunctionsPage.architecture.deleteChannelTitle'
        descriptionKey='safetyFunctionsPage.architecture.deleteChannelDescription'
        onConfirm={async () => {
          if (dialogState?.kind !== 'channel-delete') {
            return
          }
          try {
            await deleteChannel.mutateAsync(dialogState.channelId)
            toast.success(t('safetyFunctionsPage.deleteSuccess'))
            closeDialog()
          } catch (err: unknown) {
            const message =
              err instanceof Error
                ? err.message
                : t('safetyFunctionsPage.deleteFailure')
            toast.error(message)
          }
        }}
      />

      <DeleteArchitectureEntityDialog
        isOpen={
          dialogState?.kind === 'block-delete' ||
          dialogState?.kind === 'element-delete'
        }
        onClose={closeDialog}
        isDeleting={deleteNode.isPending}
        titleKey='safetyFunctionsPage.architecture.deleteNodeTitle'
        descriptionKey='safetyFunctionsPage.architecture.deleteNodeDescription'
        onConfirm={async () => {
          if (
            dialogState?.kind !== 'block-delete' &&
            dialogState?.kind !== 'element-delete'
          ) {
            return
          }
          try {
            await deleteNode.mutateAsync(dialogState.nodeId)
            toast.success(t('safetyFunctionsPage.deleteSuccess'))
            closeDialog()
          } catch (err: unknown) {
            const message =
              err instanceof Error
                ? err.message
                : t('safetyFunctionsPage.deleteFailure')
            toast.error(message)
          }
        }}
      />
    </div>
  )
}
